import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  Select,
  Input,
  Spinner,
  useToast,
  Box,
  Image,
  Divider,
  IconButton,
} from '@chakra-ui/react';
import { FiSend, FiPlus, FiLink2, FiMail, FiUser, FiX } from 'react-icons/fi';
import api from '../../services/api';
import useCreditWarning from '../../hooks/useCreditWarning';
import CreditWarningModal from '../common/CreditWarningModal';

// Official Smartlead logo (same as IntegrationsPage)
const SmartleadLogo = () => (
  <Box
    as="svg"
    viewBox="0 0 40 40"
    w="50px"
    h="50px"
  >
    <path 
      d="M0 5.03C0 2.25 2.25 0 5.03 0H34.97C37.75 0 40 2.25 40 5.03V34.97C40 37.75 37.75 40 34.97 40H5.03C2.25 40 0 37.75 0 34.97V5.03Z" 
      fill="#6E58F1"
    />
    <path 
      d="M5.77 16.92C5.77 13.47 8.56 10.67 12 10.67H12.35C13.81 10.67 14.99 11.86 14.99 13.32V20.53C14.99 21.98 13.81 23.17 12.35 23.17H12C8.56 23.17 5.77 20.37 5.77 16.92Z" 
      fill="white"
    />
    <path 
      d="M16.41 11.78C16.41 11.11 16.8 10.5 17.41 10.23L26.47 6.15C27.59 5.65 28.85 6.47 28.85 7.7V26.27C28.85 27.52 27.57 28.34 26.45 27.82L17.39 23.63C16.79 23.35 16.41 22.75 16.41 22.08V21.22C16.41 20.52 16.84 19.88 17.5 19.63L22.55 17.72C23.28 17.44 23.28 16.41 22.55 16.13L17.5 14.22C16.84 13.97 16.41 13.33 16.41 12.63V11.78Z" 
      fill="white"
    />
    <path 
      d="M34.22 18.41V18.03C34.22 15.94 32.53 14.24 30.45 14.24C30.34 14.24 30.26 14.33 30.26 14.43V22.01C30.26 22.11 30.34 22.2 30.45 22.2C32.53 22.2 34.22 20.5 34.22 18.41Z" 
      fill="white"
    />
    <path 
      d="M12 25.58C12 25.21 12.29 24.91 12.66 24.91H16.17C16.54 24.91 16.84 25.21 16.84 25.58V31.57C16.84 32.91 15.76 34 14.42 34C13.08 34 12 32.91 12 31.57V25.58Z" 
      fill="white"
    />
  </Box>
);

const SmartleadExportModal = ({
  isOpen,
  onClose,
  campaignId,
  versionId,
  onExportComplete,
}) => {
  const [campaigns, setCampaigns] = useState([]);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState('');
  
  // Preview state
  const [preview, setPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  
  const toast = useToast();

  // Credit warning hook
  const {
    withCreditWarning,
    isWarningOpen,
    warningData,
    handleConfirm,
    handleOptOut,
    closeWarning,
    userSubscriptionStatus,
  } = useCreditWarning();

  // Fetch export preview (contacts with email)
  const fetchPreview = useCallback(async () => {
    if (!campaignId || !versionId) return;
    
    setIsLoadingPreview(true);
    try {
      const response = await api.post('/api/integrations/smartlead/export-preview', {
        campaign_id: campaignId,
        version_id: versionId,
      });
      setPreview(response.data);
    } catch (error) {
      console.error('Failed to fetch export preview:', error);
      setPreview(null);
    } finally {
      setIsLoadingPreview(false);
    }
  }, [campaignId, versionId]);

  // Fetch Smartlead campaigns (+ export tracking + column-match when ids available)
  const fetchCampaigns = useCallback(async () => {
    setIsLoadingCampaigns(true);
    try {
      const params = {};
      if (campaignId) params.campaign_id = campaignId;
      if (versionId) params.version_id = versionId;
      const response = await api.get('/api/integrations/smartlead/campaigns', { params });
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Failed to fetch Smartlead campaigns:', error);
      toast({
        title: 'Failed to load campaigns',
        description: error.response?.data?.detail || 'Could not fetch Smartlead campaigns',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingCampaigns(false);
    }
  }, [toast, campaignId, versionId]);

  useEffect(() => {
    if (isOpen) {
      fetchCampaigns();
      fetchPreview();
      // Reset state
      setSelectedCampaignId('');
      setIsCreatingNew(false);
      setNewCampaignName('');
    }
  }, [isOpen, fetchCampaigns, fetchPreview]);

  const handleCampaignSelect = (e) => {
    const value = e.target.value;
    if (value === 'create_new') {
      setIsCreatingNew(true);
      setSelectedCampaignId('');
    } else {
      setIsCreatingNew(false);
      setSelectedCampaignId(value);
    }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaignName.trim()) {
      toast({
        title: 'Campaign name required',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsLoadingCampaigns(true);
    try {
      const response = await api.post('/api/integrations/smartlead/campaigns', null, {
        params: { campaign_name: newCampaignName.trim() }
      });
      
      const newCampaign = response.data.campaign;
      setCampaigns(prev => [...prev, newCampaign]);
      setSelectedCampaignId(newCampaign.id.toString());
      setIsCreatingNew(false);
      setNewCampaignName('');
      
      toast({
        title: 'Campaign created',
        description: `"${newCampaign.name}" created in Smartlead`,
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to create campaign:', error);
      toast({
        title: 'Failed to create campaign',
        description: error.response?.data?.detail || 'Could not create Smartlead campaign',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingCampaigns(false);
    }
  };

  const handleExport = async () => {
    if (!selectedCampaignId) {
      toast({
        title: 'Select a campaign',
        description: 'Please select a Smartlead campaign to export contacts to',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (!preview || preview.contacts_with_email === 0) {
      toast({
        title: 'No contacts to export',
        description: 'There are no contacts with email addresses to export',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // Get campaign name for better UX
    const campaignName = campaigns.find(c => c.id.toString() === selectedCampaignId)?.name || 'campaign';
    const contactCount = preview.contacts_with_email;

    // Wrap export in credit warning
    try {
      await withCreditWarning(
        'smartlead_export',
        {
          campaign_id: campaignId,
          version_id: versionId,
        },
        async () => {
          // Show "Export started" toast immediately
          toast({
            title: '🚀 Export started',
            description: `Exporting ${contactCount} contacts to "${campaignName}"...`,
            status: 'info',
            duration: 4000,
            isClosable: true,
          });

          // Close modal immediately (user can continue working)
          onClose();

          // Fire API call in background (don't block UI)
          try {
            const response = await api.post('/api/integrations/smartlead/export-leads', {
              campaign_id: campaignId,
              version_id: versionId,
              smartlead_campaign_id: parseInt(selectedCampaignId),
              smartlead_campaign_name: campaignName,
            });

            const exportedCount = response.data.exported_count || 0;
            
            // Show success toast when done
            toast({
              title: '✅ Export completed',
              description: `Successfully exported ${exportedCount} contacts to "${campaignName}"`,
              status: 'success',
              duration: 6000,
              isClosable: true,
            });

            onExportComplete?.();
          } catch (error) {
            console.error('Export failed:', error);
            
            // Show error toast (even if user navigated away)
            toast({
              title: '❌ Export failed',
              description: error.response?.data?.detail || 'Failed to export contacts to Smartlead',
              status: 'error',
              duration: 8000,
              isClosable: true,
            });
          }
        }
      );
    } catch (error) {
      if (!error?.isCancellation) {
        console.error('Credit warning error:', error);
      }
    }
  };

  const handleClose = () => {
    onClose();
  };

  const selectedCampaign = campaigns.find(c => c.id.toString() === selectedCampaignId);
  const hasContactsToExport = preview && preview.contacts_with_email > 0;

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="#0a0a0a" color="#fafafa" maxW="520px" border="1px solid rgba(255,255,255,0.10)" borderRadius="12px">
          <ModalHeader pb={3}>
            <HStack justify="space-between" align="center" w="full">
              <Text fontSize="lg" fontWeight="700" letterSpacing="-0.01em">Export to Smartlead</Text>
              <IconButton
                icon={<FiX />}
                onClick={handleClose}
                variant="ghost"
                color="#a3a3a3"
                size="sm"
                aria-label="Close"
                transition="all 0.2s ease"
                _hover={{ color: "#fafafa", bg: "rgba(255,255,255,0.06)", transform: "rotate(90deg)" }}
                _focus={{ boxShadow: "none" }}
              />
            </HStack>
          </ModalHeader>

          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* Logo connection visual */}
              <HStack justify="center" spacing={10} py={3}>
                <Box>
                  <Image
                    src="/logo/gillyreach_logo_white.png"
                    alt="GillyReach"
                    h="70px"
                    objectFit="contain"
                  />
                </Box>
                <Box color="rgba(255,255,255,0.25)" mr="10px">
                  <FiLink2 size={22} />
                </Box>
                <Box mt="-8px">
                  <SmartleadLogo />
                </Box>
              </HStack>

              {/* Preview section */}
              <Box bg="#111111" p={4} borderRadius="10px" border="1px solid rgba(255,255,255,0.08)">
                {isLoadingPreview ? (
                  <HStack justify="center" py={2}>
                    <Spinner size="sm" color="rgba(255,255,255,0.4)" />
                    <Text fontSize="sm" color="#a3a3a3">Loading contacts...</Text>
                  </HStack>
                ) : preview ? (
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <HStack spacing={2} color="#a3a3a3">
                        <FiMail size={14} />
                        <Text fontSize="sm">Contacts with email</Text>
                      </HStack>
                      <Box
                        px={2.5}
                        py="2px"
                        borderRadius="full"
                        bg={hasContactsToExport ? "rgba(74,222,128,0.1)" : "rgba(239,68,68,0.1)"}
                        border={`1px solid ${hasContactsToExport ? "rgba(74,222,128,0.25)" : "rgba(239,68,68,0.25)"}`}
                      >
                        <Text fontSize="xs" fontWeight="600" color={hasContactsToExport ? "#4ade80" : "rgba(239,68,68,0.85)"}>
                          {preview.contacts_with_email} / {preview.total_contacts}
                        </Text>
                      </Box>
                    </HStack>

                    {hasContactsToExport && preview.sample && preview.sample.length > 0 && (
                      <>
                        <Divider borderColor="rgba(255,255,255,0.08)" />
                        <Text fontSize="xs" color="rgba(255,255,255,0.3)">Preview:</Text>
                        <VStack align="stretch" spacing={1}>
                          {preview.sample.map((contact, idx) => (
                            <HStack key={idx} fontSize="xs" color="rgba(255,255,255,0.55)">
                              <FiUser size={11} />
                              <Text noOfLines={1}>
                                {contact.name} — <Text as="span" color="rgba(255,255,255,0.35)">{contact.email}</Text>
                              </Text>
                            </HStack>
                          ))}
                          {preview.contacts_with_email > 5 && (
                            <Text fontSize="xs" color="rgba(255,255,255,0.25)" pl={4}>
                              ...and {preview.contacts_with_email - 5} more
                            </Text>
                          )}
                        </VStack>
                      </>
                    )}

                    {!hasContactsToExport && (
                      <Text fontSize="sm" color="rgba(251,146,60,0.8)">
                        No contacts have email addresses. Enrich contacts first.
                      </Text>
                    )}
                  </VStack>
                ) : (
                  <Text fontSize="sm" color="#a3a3a3" textAlign="center">Unable to load preview</Text>
                )}
              </Box>

              {/* Campaign selection */}
              <VStack align="stretch" spacing={3}>
                <Text fontSize="sm" fontWeight="500" color="#a3a3a3">Select Smartlead Campaign</Text>

                {isLoadingCampaigns && !isCreatingNew ? (
                  <HStack justify="center" py={4}>
                    <Spinner size="sm" color="rgba(255,255,255,0.4)" />
                    <Text fontSize="sm" color="#a3a3a3">Loading campaigns...</Text>
                  </HStack>
                ) : (
                  <>
                    <Select
                      placeholder="Choose a campaign..."
                      value={isCreatingNew ? 'create_new' : selectedCampaignId}
                      onChange={handleCampaignSelect}
                      bg="#111111"
                      borderColor="rgba(255,255,255,0.12)"
                      color="#fafafa"
                      fontSize="sm"
                      _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                      _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                      isDisabled={!hasContactsToExport}
                    >
                      <option value="create_new" style={{ background: '#111111' }}>
                        ➕ Create New Campaign
                      </option>
                      {campaigns.map((campaign) => {
                        const info = campaign.export_info;
                        const isBlocked = info && preview && (
                          info.exported_count >= preview.contacts_with_email
                          || info.field_definitions_match === false
                        );
                        const label = !info
                          ? campaign.name
                          : info.field_definitions_match === false
                            ? `${campaign.name}  ⚠ Columns changed`
                            : info.exported_count >= (preview?.contacts_with_email ?? 0)
                              ? `${campaign.name}  ✓ Already exported`
                              : campaign.name;

                        return (
                          <option
                            key={campaign.id}
                            value={campaign.id}
                            disabled={isBlocked}
                            style={{ background: '#111111' }}
                          >
                            {label}
                          </option>
                        );
                      })}
                    </Select>

                    {isCreatingNew && (
                      <VStack align="stretch" spacing={2} pl={3} borderLeft="2px solid rgba(255,255,255,0.15)">
                        <Input
                          placeholder="Enter new campaign name..."
                          value={newCampaignName}
                          onChange={(e) => setNewCampaignName(e.target.value)}
                          bg="#111111"
                          borderColor="rgba(255,255,255,0.12)"
                          color="#fafafa"
                          fontSize="sm"
                          _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
                          _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                          _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                          isDisabled={isLoadingCampaigns}
                        />
                        <HStack>
                          <Button
                            size="sm"
                            leftIcon={<FiPlus />}
                            bg="#fafafa"
                            color="#0a0a0a"
                            borderRadius="full"
                            fontWeight="600"
                            _hover={{ opacity: 0.85 }}
                            onClick={handleCreateCampaign}
                            isLoading={isLoadingCampaigns}
                            isDisabled={!newCampaignName.trim()}
                          >
                            Create
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            color="#a3a3a3"
                            borderRadius="full"
                            _hover={{ color: "#fafafa", bg: "rgba(255,255,255,0.06)" }}
                            _focus={{ boxShadow: "none" }}
                            onClick={() => {
                              setIsCreatingNew(false);
                              setNewCampaignName('');
                            }}
                            isDisabled={isLoadingCampaigns}
                          >
                            Cancel
                          </Button>
                        </HStack>
                      </VStack>
                    )}
                  </>
                )}
              </VStack>

              {selectedCampaign && (
                <Box bg="rgba(255,255,255,0.02)" p={3} borderRadius="10px" border="1px solid rgba(255,255,255,0.08)">
                  <Text fontSize="sm" color="#a3a3a3">
                    Exporting to: <Text as="span" color="#fafafa" fontWeight="500">{selectedCampaign.name}</Text>
                  </Text>
                </Box>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter gap={2}>
            <Button
              variant="ghost"
              color="#a3a3a3"
              borderRadius="full"
              fontSize="sm"
              _hover={{ color: "#fafafa", bg: "rgba(255,255,255,0.06)" }}
              _focus={{ boxShadow: "none" }}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              leftIcon={<FiSend size={14} />}
              bg="#fafafa"
              color="#0a0a0a"
              borderRadius="full"
              fontWeight="600"
              fontSize="sm"
              _hover={{ opacity: 0.85 }}
              onClick={handleExport}
              isDisabled={!selectedCampaignId || !hasContactsToExport}
            >
              Export {hasContactsToExport ? `${preview.contacts_with_email} Contacts` : ''}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Credit Warning Modal */}
      {warningData && (
        <CreditWarningModal
          isOpen={isWarningOpen}
          onClose={closeWarning}
          operationType={warningData.operation_type}
          creditsNeeded={warningData.credits_needed}
          currentBalance={warningData.balance}
          onConfirm={handleConfirm}
          onOptOut={handleOptOut}
          userSubscriptionStatus={userSubscriptionStatus}
          allowsOverageProceed={warningData.preview_allows_overage_proceed === true}
          overageCreditsForRequest={warningData.overage_credits_for_request}
          overagePricePerCreditCents={warningData.overage_price_per_credit_cents}
        />
      )}
    </>
  );
};

export default SmartleadExportModal;
